import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import PublicHeader from '../../components/ui/PublicHeader';
import NewsletterCard from '../../components/ui/NewsletterCard';
import FilterPanel from './components/FilterPanel';
import PaginationControls from './components/PaginationControls';
import EmptyState from './components/EmptyState';
import newsletterService from '../../services/newsletterService';

const NewsletterArchive = () => {
  const navigate = useNavigate();
  const [newsletters, setNewsletters] = useState([]);
  const [filteredNewsletters, setFilteredNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const itemsPerPage = 9;

  useEffect(() => {
    loadNewsletters();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [newsletters, selectedTags, searchQuery]);

  const loadNewsletters = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await newsletterService?.getPublished();
      setNewsletters(data);
    } catch (err) {
      setError(err?.message || 'Failed to load newsletters');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...newsletters];

    // Apply tag filter
    if (selectedTags?.length > 0) {
      filtered = filtered?.filter(newsletter =>
        selectedTags?.some(tag => newsletter?.tags?.includes(tag))
      );
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(newsletter =>
        newsletter?.title?.toLowerCase()?.includes(query) ||
        newsletter?.excerpt?.toLowerCase()?.includes(query)
      );
    }

    setFilteredNewsletters(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleTagToggle = (tag) => {
    setSelectedTags(prev =>
      prev?.includes(tag)
        ? prev?.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleClearFilters = () => {
    setSelectedTags([]);
    setSearchQuery('');
  };

  const handleNewsletterClick = (newsletter) => {
    navigate(`/newsletter/${newsletter?.slug}`);
  };

  // Get all unique tags from newsletters
  const allTags = [...new Set(newsletters?.flatMap(n => n?.tags || []))];

  // Pagination
  const totalPages = Math.ceil(filteredNewsletters?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentNewsletters = filteredNewsletters?.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <PublicHeader />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading newsletters...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <main className="container mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground mb-4">
            Newsletter Archive
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Browse our complete collection of newsletters featuring funding opportunities, research insights, and expert guidance
          </p>
        </div>

        {error && (
          <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-red-900">Error Loading Newsletters</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <aside className="lg:col-span-1">
            <FilterPanel
              selectedTags={selectedTags}
              allTags={allTags}
              searchQuery={searchQuery}
              onTagToggle={handleTagToggle}
              onSearchChange={setSearchQuery}
              onClearFilters={handleClearFilters}
              selectedTopic=""
              onTopicChange={() => {}}
              selectedYear=""
              onYearChange={() => {}}
              sortOrder=""
              onSortChange={() => {}}
              totalResults={filteredNewsletters?.length}
              isExpanded={true}
              onToggleExpanded={() => {}}
            />
          </aside>

          <div className="lg:col-span-3">
            {currentNewsletters?.length === 0 ? (
              <EmptyState
                hasFilters={selectedTags?.length > 0 || searchQuery?.length > 0}
                onClearFilters={handleClearFilters}
              />
            ) : (
              <>
                <div className="mb-6 flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Showing {startIndex + 1}-{Math.min(endIndex, filteredNewsletters?.length)} of {filteredNewsletters?.length} newsletters
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
                  {currentNewsletters?.map((newsletter) => (
                    <NewsletterCard
                      key={newsletter?.id}
                      newsletter={newsletter}
                      onClick={() => handleNewsletterClick(newsletter)}
                      id={newsletter?.id}
                      slug={newsletter?.slug}
                      title={newsletter?.title || ''}
                      date={newsletter?.date || newsletter?.publishedAt || ''}
                      summary={newsletter?.excerpt || newsletter?.summary || ''}
                      tags={newsletter?.tags || []}
                      onEdit={() => {}}
                      onDelete={() => {}}
                      onPublish={() => {}}
                    />
                  ))}
                </div>

                {totalPages > 1 && (
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    itemsPerPage={itemsPerPage}
                    totalItems={filteredNewsletters?.length}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default NewsletterArchive;